import { IComboPlusFragment, IItemFragment } from 'generated/sanity-graphql';
import {
  LoyaltyEngineId,
  SanityOfferListItemWithExtras,
  SanitySystemwideOffer,
  SanitySystemwideTierGroup,
  isSanitySystemwideOffer,
  isSanitySystemwideTierGroup,
} from 'state/loyalty/types';

type PriceTargets = {
  itemsPrice: number;
  idealPrice: number;
  maxPrice: number;
  minPrice: number;
};

export type ValidSystemwideTierGroup = {
  tierGroupName: string;
  discount_rate: number;
  min_discount_rate: number;
  max_discount_rate: number;
  offers: SanitySystemwideOffer[];
  menu_items: Mutable<IComboPlusFragment | IItemFragment>[];
};

export function getPriceTargets(
  tierGroup: ValidSystemwideTierGroup,
  itemsPrice: number
): PriceTargets {
  const { discount_rate, min_discount_rate, max_discount_rate } = tierGroup;

  return {
    itemsPrice,
    idealPrice: itemsPrice * (1 - discount_rate / 100),
    maxPrice: itemsPrice * (1 - min_discount_rate / 100),
    minPrice: itemsPrice * (1 - max_discount_rate / 100),
  };
}

type Mutable<T> = { -readonly [P in keyof T]: T[P] };

/**
 * Validates and ensures a tier group fragment is a usable SystemwideTierGroup object.
 *
 * This function performs comprehensive validation of tier group data and returns either a valid
 * tier group object or a descriptive error message. Consumers must check the return type using
 * array destructuring to determine if validation succeeded.
 *
 * **Validation Rules:**
 * - All discount rates must be numbers between 0-100
 * - `min_discount_rate` must be less than `discount_rate`
 * - `max_discount_rate` must be greater than `discount_rate`
 * - Must have at least 1 valid offer (after filtering invalid/null entries)
 * - Must have at least 1 valid menu item (filters out invalid/null items)
 *
 * @param input Raw tier group fragment from GraphQL/Sanity, may be undefined
 * @returns Tuple: [SystemwideTierGroup] if valid, or [null, error message string] if invalid
 */
export function validateTierGroup(
  input: SanitySystemwideTierGroup | undefined
): [ValidSystemwideTierGroup] | [null, string] {
  if (!input) {
    return [null, 'Invalid tier group: undefined input'];
  }

  const {
    offers,
    discount_rate,
    min_discount_rate,
    max_discount_rate,
    menu_items,
    tierGroupName,
  } = input;

  if (typeof discount_rate !== 'number' || discount_rate < 0 || discount_rate > 100) {
    return [null, `Invalid discount_rate: ${discount_rate}`];
  }
  if (typeof min_discount_rate !== 'number' || min_discount_rate < 0 || min_discount_rate > 100) {
    return [null, `Invalid min_discount_rate: ${min_discount_rate}`];
  }
  if (typeof max_discount_rate !== 'number' || max_discount_rate < 0 || max_discount_rate > 100) {
    return [null, `Invalid max_discount_rate: ${max_discount_rate}`];
  }
  if (min_discount_rate >= discount_rate) {
    return [
      null,
      `min_discount_rate (${min_discount_rate}) must be less than discount_rate (${discount_rate})`,
    ];
  }
  if (max_discount_rate <= discount_rate) {
    return [
      null,
      `max_discount_rate (${max_discount_rate}) must be greater than discount_rate (${discount_rate})`,
    ];
  }
  if (!offers || offers.length < 1) {
    return [null, `Invalid offers: must have at least 1 offer, got ${offers?.length ?? 0}`];
  }
  if (!menu_items || !menu_items.length || menu_items.length < 1) {
    return [
      null,
      `Invalid menu_items: must have at least 1 menu item, got ${
        menu_items ? menu_items.length : 0
      }`,
    ];
  }

  const filteredOffers = offers.filter(isSanitySystemwideOffer);

  const filteredMenuItems = menu_items?.filter(Boolean) as Mutable<
    IComboPlusFragment | IItemFragment
  >[];

  if (filteredOffers.length < 1) {
    return [
      null,
      `Filtered offers: must have at least 1 valid offer, got ${filteredOffers.length}`,
    ];
  }
  if (filteredMenuItems.length < 1) {
    return [
      null,
      `Filtered menu_items: must have at least 1 valid menu item, got ${filteredMenuItems.length}`,
    ];
  }

  return [
    {
      tierGroupName: tierGroupName || 'Unnamed Tier Group',
      discount_rate,
      min_discount_rate,
      max_discount_rate,
      offers: filteredOffers,
      menu_items: filteredMenuItems,
    },
  ];
}

/**
 * Selects the best offer from a list of offers with prices, based on business rules:
 *
 * 1. If there are no offers, returns a string error message.
 * 2. If no offers are found below the items price, returns a string error message.
 * 3. If no offers fall within the allowed price range (minPrice to maxPrice), returns a string error message.
 * 4. Otherwise, finds the offer(s) whose price is closest to the ideal target price (based on the tier's discount rate).
 *    - If there is a single closest offer, returns it.
 *    - If multiple offers are equally close, returns the one with the highest price (maximizing revenue).
 *
 * This ensures the selected offer is both attractive to the customer and optimal for business margins.
 *
 * @returns Tuple: [SystemwideOffer] if found, or [null, error message string] otherwise.
 */
export function selectOffer(
  priceTargets: PriceTargets,
  offersWithPrice: OfferWithPrice[]
): [SanitySystemwideOffer] | [null, string] {
  const { itemsPrice, idealPrice, maxPrice, minPrice } = priceTargets;

  if (offersWithPrice.length === 0) {
    return [null, 'No offers available'];
  }

  // If there are no offers below the items price, return null
  if (offersWithPrice.every(offer => offer.price >= itemsPrice)) {
    return [null, 'No offers below the items price'];
  }

  // If there are no offers within the min and max price range, return null
  if (offersWithPrice.every(offer => offer.price < minPrice || offer.price > maxPrice)) {
    return [null, 'No offers within the min and max price range'];
  }

  // Find the offer(s) closest to the ideal price
  const offersWithDeltas = offersWithPrice.map(({ offer, price }) => ({
    offer,
    price,
    delta: Math.abs(price - idealPrice),
  }));
  const minDelta = Math.min(...offersWithDeltas.map(d => d.delta));
  const closestOffers = offersWithDeltas.filter(d => d.delta === minDelta);

  // If only one offer is closest, return it
  if (closestOffers.length === 1) {
    return [{ ...closestOffers[0].offer, isTieredOffer: true }];
  }

  // If more than one offer is equally close, return the highest-priced one
  let highestPricedOffer = closestOffers[0];
  for (const offer of closestOffers) {
    if (offer.price > highestPricedOffer.price) {
      highestPricedOffer = offer;
    }
  }

  return [{ ...highestPricedOffer.offer, isTieredOffer: true }];
}

export type OfferWithPrice = {
  offer: ValidSystemwideTierGroup['offers'][number];
  price: number;
};

// Create a lookup map of all offers that belong to tier groups.
// This is used later to identify and filter out non-selected tier group offers
// during the final transformation step.
export function buildTierGroupOfferLookup(items: SanityOfferListItemWithExtras[]) {
  return (
    items
      // Filter to only tier group items (excluding regular offers)
      .filter(isSanitySystemwideTierGroup)
      // Extract all offers from each tier group into a flat array
      .flatMap(tierGroup => tierGroup.offers || [])
      // Build a lookup map indexed by loyaltyEngineId for fast membership checks
      .reduce((map, offer) => {
        if (offer?.loyaltyEngineId) {
          map[offer.loyaltyEngineId] = offer;
        }
        return map;
      }, {} as Record<LoyaltyEngineId, SanitySystemwideOffer>)
  );
}

export function shouldExcludeNonSelectedTierGroupOffer(
  offer: SanitySystemwideOffer,
  tierGroupOfferLookup: Record<LoyaltyEngineId, SanitySystemwideOffer>
): boolean {
  // Check if this offer belongs to any tier group
  if (tierGroupOfferLookup[offer.loyaltyEngineId]) {
    // If it belongs to a tier group but doesn't have isTieredOffer flag,
    // it means it wasn't selected and should be excluded
    const shouldExclude = !('isTieredOffer' in offer) || offer.isTieredOffer === false;
    return shouldExclude;
  }

  // If it doesn't belong to any tier group, include it
  return false;
}
